import React, { useEffect, useMemo, useState } from "react";
import { NormalModal } from "@/components/modals/NormalModal";
import { axiosInstance } from "@/utilities";
import { API_URL_PIM_BULK } from "@/services/XCONS_ENV";
import { debounce } from "@/utilities/debounce";
import { AgregarOperador, type Nivel } from "./CatalogAdminsDrawer";
import type { ProductParticipantCandidate } from "./types";
import { NIVEL } from "@/utilities/niveles-de-permiso";

/**
 * Los tres niveles del producto, como en Agregar participante del panel (xcons-docs#594).
 * **Admin la deja dueña** de cada producto, como siempre (xcons-docs#462); **Editor y
 * Lector la suman como participante** al final, sin tocar al dueño (Lector desde
 * xcons-docs#603).
 */
const NIVELES: Nivel[] = [
  {
    value: "admin",
    label: NIVEL.admin,
    hint: "Editar, publicar, aprobar y archivar este producto, y sumar participantes",
  },
  {
    value: "editor",
    label: NIVEL.editor,
    hint: "Editar este producto",
  },
  {
    value: "viewer",
    label: NIVEL.viewer,
  },
];

/** Lo que el lote acepta como nivel: cualquier otro valor cae en Admin, el de siempre. */
type NivelDelLote = "admin" | "editor" | "viewer";

const nivelDelLote = (level: string): NivelDelLote =>
  level === "editor" || level === "viewer" ? level : "admin";

/**
 * Asignar dueño en lote (xcons-docs#433, backend en xcons-docs#430). La persona queda en
 * `position=0` y como Admin de cada producto elegido: lo mismo que el panel de Permisos
 * hace de a uno arrastrándola al primer lugar.
 *
 * **Es el popup de Agregar participante** (xcons-docs#462): el mismo `AgregarOperador`, con
 * Admin, Editor o Lector (xcons-docs#594, #603). Sin avisos: lo que hacían explicar ya lo dice el título.
 *
 * **Los candidatos salen del mismo buscador que Participantes**, y no del de Contactos:
 * sólo operadores que entran al Catálogo y están en el alcance de quien asigna
 * (xcons-docs#443). El backend vuelve a chequear las dos cosas y contesta
 * `OWNER_NOT_ELIGIBLE` si alguien llega por fuera de esta lista.
 *
 * Se manda el `userId` y no el `contactId`: el dueño es una persona, y la ficha con la que
 * se guarda la elige el backend (la canónica).
 */
export const AsignarDueno: React.FC<{
  open: boolean;
  onCancelar: () => void;
  /** Con el nombre, para que el aviso diga a quién se agregó (xcons-docs#487). */
  onConfirmar: (
    userId: number,
    nombre: string,
    level: NivelDelLote,
  ) => Promise<void>;
}> = ({ open, onCancelar, onConfirmar }) => {
  const [candidatos, setCandidatos] = useState<ProductParticipantCandidate[]>(
    [],
  );
  const [buscando, setBuscando] = useState(false);
  const [guardando, setGuardando] = useState(false);

  const buscar = useMemo(
    () =>
      debounce(async (texto: string) => {
        setBuscando(true);
        try {
          const { data } = await axiosInstance.get<
            ProductParticipantCandidate[]
          >(`${API_URL_PIM_BULK}/owner-candidates`, { params: { q: texto } });
          setCandidatos(data ?? []);
        } catch {
          setCandidatos([]);
        } finally {
          setBuscando(false);
        }
      }, 350),
    [],
  );

  // Cada vez que se abre, con la primera página de candidatos ya cargada.
  useEffect(() => {
    if (open) buscar("");
  }, [open, buscar]);

  return (
    <NormalModal
      footer={null}
      destroyOnClose
      open={open}
      onCancel={onCancelar}
      title="Agregar participantes"
    >
      <AgregarOperador
        niveles={NIVELES}
        nivelInicial="admin"
        candidatos={candidatos}
        buscando={buscando}
        guardando={guardando}
        onSearch={buscar}
        onVolver={onCancelar}
        onGuardar={async (elegido, level) => {
          setGuardando(true);
          try {
            await onConfirmar(
              elegido.userId,
              elegido.name,
              nivelDelLote(level),
            );
          } finally {
            setGuardando(false);
          }
        }}
      />
    </NormalModal>
  );
};
